import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { HandleAxiosPromise } from "@shared_components/axios/HandleAxiosPromise";
import { useMessage } from "@shared_components/providers/MessageProvider";
import { FETCH_GEARSTUDIO_HISTORY_URL, GearStudioDesign } from "@src/types";
import { useGearstudioAuth } from "./GearstudioAuthProvider";

type FetchHistoryArgs = {
    offset: number;
    append: boolean;
};

type GearstudioHistoryContextType = {
    historyDesigns: GearStudioDesign[];
    loadingHistory: boolean;
    hasMoreHistory: boolean;
    fetchHistory: (args: FetchHistoryArgs) => void;
    loadMoreHistory: () => void;
    prependHistoryDesigns: (designs: GearStudioDesign[]) => void;
};

const HISTORY_PAGE_SIZE = 8;

const normalizeDesigns = (payload: any): GearStudioDesign[] => {
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    if (Array.isArray(payload?.results)) {
        return payload.results;
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    return [];
};

const mergeUniqueDesigns = (primary: GearStudioDesign[], secondary: GearStudioDesign[]) => {
    const seenIds = new Set<number>();
    const merged: GearStudioDesign[] = [];

    [...primary, ...secondary].forEach((design) => {
        if (!design || typeof design.id !== "number" || seenIds.has(design.id)) {
            return;
        }
        seenIds.add(design.id);
        merged.push(design);
    });

    return merged;
};

const GearstudioHistoryContext = createContext<GearstudioHistoryContextType | undefined>(undefined);

export const GearstudioHistoryProvider = ({ children }: { children: React.ReactNode }) => {
    const { setTextData } = useMessage();
    const { loggedIn, user } = useGearstudioAuth();

    const [historyDesigns, setHistoryDesigns] = useState<GearStudioDesign[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [historyOffset, setHistoryOffset] = useState(0);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);

    const fetchHistory = useCallback(({ offset, append }: FetchHistoryArgs) => {
        if (!loggedIn || !user?.id) {
            return;
        }
        setLoadingHistory(true);
        HandleAxiosPromise({
            url: FETCH_GEARSTUDIO_HISTORY_URL,
            method: "get",
            data: { offset, limit: HISTORY_PAGE_SIZE },
            additional_headers: { "User-Id": user.id },
        }).then((response) => {
            if (response?.status !== 200) {
                console.error("Unexpected response while fetching design history:", response);
                setTextData({ text: "Failed to load design history. Please try again later.", variant: "danger" });
                return;
            }
            const payload = response?.data || {};
            const parsedDesigns = normalizeDesigns(payload);
            const next = payload?.next;

            setHistoryDesigns((previous) => (append ? mergeUniqueDesigns(previous, parsedDesigns) : parsedDesigns));
            setHistoryOffset(offset);
            setHasMoreHistory(Boolean(next) || parsedDesigns.length === HISTORY_PAGE_SIZE);
        }).catch((error) => {
            console.error("Failed to fetch design history:", error);
            setTextData({ text: "Failed to load design history. Please try again later.", variant: "danger" });
        }).finally(() => {
            setLoadingHistory(false);
        });
    }, [loggedIn, setTextData, user?.id]);

    const loadMoreHistory = useCallback(() => {
        if (loadingHistory || !hasMoreHistory) {
            return;
        }
        fetchHistory({ offset: historyOffset + HISTORY_PAGE_SIZE, append: true });
    }, [fetchHistory, hasMoreHistory, historyOffset, loadingHistory]);

    const prependHistoryDesigns = useCallback((designs: GearStudioDesign[]) => {
        if (!Array.isArray(designs) || designs.length === 0) {
            return;
        }
        setHistoryDesigns((previous) => mergeUniqueDesigns(designs, previous));
    }, []);

    useEffect(() => {
        if (!loggedIn  || !user?.id) {
            setHistoryDesigns([]);
            setHasMoreHistory(false);
            setHistoryOffset(0);
            return;
        }

        setHistoryDesigns([]);
        setHasMoreHistory(true);
        setHistoryOffset(0);
        fetchHistory({ offset: 0, append: false });
    }, [ fetchHistory, loggedIn, user?.id]);

    const value = useMemo(() => ({

        historyDesigns,
        loadingHistory,
        hasMoreHistory,
        fetchHistory,
        loadMoreHistory,
        prependHistoryDesigns,
    }), [historyDesigns, loadingHistory, hasMoreHistory, fetchHistory, loadMoreHistory, prependHistoryDesigns]);

    return (
        <GearstudioHistoryContext.Provider value={value}>
            {children}
        </GearstudioHistoryContext.Provider>
    );
};

export const useGearstudioHistory = () => {
    const context = useContext(GearstudioHistoryContext);
    if (!context) {
        throw new Error("useGearstudioHistory must be used within GearstudioHistoryProvider.");
    }
    return context;
};
